"use client";
import { Image } from "@/components/Image";
import { dayjs } from "@/lib/dayjs";
import { Fragment, useState } from "react";
import type { CalendarJourneys } from "../getCalendarJourneys";
import type { LivestormCalendarConferences } from "../getLivestormCalendarConferences";
import type { YurplanCalendarConferences } from "../getYurplanCalendarConferences";
import { Conference } from "./Conference";
import { Filters } from "./Filters";
import { Journey } from "./Journey";

export type CalendarFilter = "Journées culturelles" | "Promenades culturelles" | "Conférences" | "Café-Conférences";

const images = {
  0: "JANVIER_conf.jpg",
  1: "FÉVRIER_conf.jpg",
  2: "MARS_conf.jpg",
  3: "AVRIL_conf.jpg",
  4: "MAI_conf.jpg",
  5: "JUIN_conf.jpg",
  6: "JUILLET_conf.jpg",
  7: "AOUT_conf.jpg",
  8: "SEPTEMBRE_conf.jpg",
  9: "OCTOBRE_conf.jpg",
  10: "NOVEMBRE_conf.jpg",
  11: "DÉCEMBRE_conf.jpg",
};

type Props = {
  yurplanCalendarConferences: YurplanCalendarConferences;
  livestormCalendarConferences: LivestormCalendarConferences;
  calendarJourneys: CalendarJourneys;
};
export const Calendar = ({ yurplanCalendarConferences, livestormCalendarConferences, calendarJourneys }: Props) => {
  const [filters, setFilters] = useState<Array<CalendarFilter>>([
    "Conférences",
    "Café-Conférences",
    "Journées culturelles",
    "Promenades culturelles",
  ]);
  const [dates, setDates] = useState<Array<dayjs.Dayjs>>([]);

  const intermedes = yurplanCalendarConferences.filter((c) => c.type === "intermedes");
  const promenades = yurplanCalendarConferences.filter((c) => c.type === "promenades");
  const cafes = yurplanCalendarConferences.filter((c) => c.type === "cafe");
  const livestorm = livestormCalendarConferences;

  const calendarJournees =
    calendarJourneys.map((j) => ({
      ...j,
      dateStart: j.departures[0]?.dateStart,
    })) || [];

  const calendarConferences = [...intermedes, ...livestorm];

  const events = [
    ...(filters.includes("Journées culturelles") ? calendarJournees : []),
    ...(filters.includes("Conférences") ? calendarConferences : []),
    ...(filters.includes("Café-Conférences") ? cafes : []),
    ...(filters.includes("Promenades culturelles") ? promenades : []),
  ].sort((a, b) => dayjs(a.dateStart).unix() - dayjs(b.dateStart).unix());

  const all = events.reduce<{
    [id: string]: typeof events;
  }>((prev, cur) => {
    const startOfMonth = dayjs(cur.dateStart).startOf("month").toISOString();

    if (startOfMonth in prev) {
      const tmp = prev[startOfMonth];
      if (tmp) {
        prev[startOfMonth] = [...tmp, cur];
      }
    } else {
      prev[startOfMonth] = [cur];
    }

    return prev;
  }, {});

  const allDates = Object.keys(all)
    .map((a) => dayjs(a))
    .sort((a, b) => a.unix() - b.unix());

  return (
    <>
      <Filters filters={filters} setFilters={setFilters} allDates={allDates} dates={dates} setDates={setDates} />
      {Object.entries(all)
        .sort((a, b) => dayjs(a[0]).unix() - dayjs(b[0]).unix())
        .map(([key, values]) => {
          if (dates.length !== 0 && !dates.some((date) => date.isSame(key, "year") && date.isSame(key, "month")))
            return null;

          const image = images[dayjs(key).month() as keyof typeof images];

          return (
            <div key={key} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-28">
              <div className="overflow-hidden relative">
                <Image
                  image={{
                    type: "asset",
                    name: image,
                    realname: image,
                  }}
                  alt="Conférences Replay"
                  height={{ type: "ratio", value: 100 }}
                  sizes="600px"
                  objectFit="cover"
                />
                <p className="absolute top-0 right-0 w-full h-full bg-pr-1/40 text-white text-3xl font-semibold grid place-items-center uppercase">
                  {dayjs(key).format("MMMM")}
                </p>
              </div>
              {values.map((c, index) => (
                <Fragment key={`${c.id}-${index}`}>
                  {"alias" in c && <Journey travel={c} />}
                  {"speaker" in c && <Conference conference={c} />}
                </Fragment>
              ))}
            </div>
          );
        })}
    </>
  );
};
